import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { cn } from '@/lib/utils'
import { easeOutExpo } from '@/lib/motion'
import { useIntro } from '@/components/generals/intro'

type RevealTag = 'h1' | 'h2' | 'h3' | 'p' | 'div' | 'blockquote'

interface RevealLinesProps {
    lines: Array<ReactNode>
    as?: RevealTag
    id?: string
    className?: string
    lineClassName?: string | ((index: number) => string)
    delay?: number
    stagger?: number
    play?: boolean
}

export const RevealLines: FC<RevealLinesProps> = ({
    lines,
    as: Tag = 'div',
    id,
    className,
    lineClassName,
    delay = 0,
    stagger = 0.085,
    play
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const { done } = useIntro()
    const inView = useInView(ref, { once: true, margin: '0px 0px -8% 0px' })
    const active = (play ?? inView) && done

    return (
        <div ref={ref}>
            <Tag id={id} className={className}>
                {lines.map((line, index) => (
                    <span
                        key={index}
                        className={cn(
                            '-mx-[0.06em] -mb-[0.1em] block overflow-hidden px-[0.06em] pb-[0.1em]',
                            typeof lineClassName === 'function' ? lineClassName(index) : lineClassName
                        )}
                    >
                        <motion.span
                            className='block'
                            initial={{ y: '108%' }}
                            animate={active ? { y: '0%' } : undefined}
                            transition={{ duration: 1.15, ease: easeOutExpo, delay: delay + index * stagger }}
                        >
                            {line}
                        </motion.span>
                    </span>
                ))}
            </Tag>
        </div>
    )
}
